import React from "react";

import { Box, Stack, Typography, useTheme } from "@mui/material";
import { CircleCheckBigIcon } from "lucide-react";

import { Button, Card } from "@repo/ui";

/**
 * The contact-sales tier (VRG-393), as a CARD below the comparison grid rather
 * than a fifth column in it.
 *
 * ## Why not a column
 *
 * Vergence — the reference for this whole layout — does not put Enterprise in its
 * matrix either: `upgrade/page.tsx` renders it as a separate narrative section, and
 * its marketing site drops it from the card grid outright (`tiers.slice(1)`). Four
 * reasons, in the order they bite:
 *
 * 1. The PRICE is the loudest element in every column, and this tier has none. A
 *    blank or "Custom" where its neighbours show `$999` reads as broken.
 * 2. Its cells would be "Custom" or ✓-everything — low information. Worse, any
 *    that rendered as `—` would make it look like a WORSE tier than Scale.
 * 3. Measured: a fifth column takes every column from ~196px to ~160px, and the
 *    plan descriptions are ALREADY hitting their two-line clamp at the current
 *    width.
 * 4. What actually justifies this tier is prose — an SLA, data residency,
 *    procurement — none of which fits in a checkmark cell.
 *
 * ## Why it looks like this
 *
 * A PLAIN card, same as every other card on the page. A brand-tinted border and
 * fill were tried and removed: nothing else in this product is treated that way, so
 * it did not read as "premium", it read as a different KIND of object — and worse,
 * brand tint on this page already means "your current plan" (the highlighted
 * column). A second brand-tinted surface a few hundred pixels below made the same
 * colour mean two things on one screen.
 *
 * What makes it interesting is composition, not decoration:
 *
 * - The features run as a TWO-COLUMN grid, so the card uses its width instead of
 *   leaving two-thirds of it empty with the CTA stranded across the gap.
 * - A divider separates the pitch from the specifics, so it reads as two blocks
 *   rather than one long run of text.
 * - The CTA sits on the TITLE's line, not floating against the whole card, so the
 *   top row reads "Enterprise … Contact sales" as one statement.
 * - "Custom pricing" occupies the slot where every column above shows a number. The
 *   eye arrives expecting a price; answering the question beats leaving a hole
 *   where the answer goes.
 *
 * ## What is listed here, and what is deliberately not
 *
 * Only things the priced tiers genuinely cannot self-serve at any price. Volume is
 * NOT among them — graduated activation pricing already scales past Scale cleanly
 * (VRG-375), so "more usage" is not a reason to call sales, and saying it would be
 * a lie a customer discovers on their first invoice. None of these four appear as
 * matrix rows (checked), so the card adds information rather than restating it.
 *
 * **SSO / SAML is deliberately absent.** It is the most natural-looking item on a
 * list like this and it is NOT BUILT. Vergence ships exactly that mistake — its
 * matrix advertises SAML while its own strategy doc records it as unimplemented.
 * Add it here the day it exists, not before.
 */
const ENTERPRISE_INCLUDES = [
  "Uptime SLA and priority response times",
  "Data residency options",
  "Custom contract terms, security review and DPA",
  "Invoicing and purchase orders",
];

interface EnterpriseContactCardProps {
  /**
   * The tier this one extends, by display title — "Everything in Scale, plus".
   * Passed rather than hardcoded because it is Stripe-authored copy: it was
   * "Enterprise" until 2026-08-24 and renaming it in the dashboard must not leave
   * this sentence naming a tier that no longer exists (VRG-393).
   */
  topPricedPlanTitle?: string | null;
  /**
   * What the CTA does. A prop because the product has NO sales channel yet — no
   * sales address, no booking link, no contact route (searched). The button
   * briefly routed to the support intake, which is wrong: a sales enquiry would
   * land in the support queue for a human to re-route. The page owns the decision;
   * this component owns how it looks.
   */
  onContact?: () => void;
}

export const EnterpriseContactCard = ({
  topPricedPlanTitle,
  onContact,
}: EnterpriseContactCardProps) => {
  const theme = useTheme();

  return (
    <Card id="enterprise-contact" sx={{ mt: 8 }}>
      {/* TITLE ROW — the CTA sits on this line, not against the whole card, so the
          top row reads "Enterprise … Contact sales" as one statement. Previously the
          button was aligned to the full card height and floated opposite the middle
          of the feature grid, anchored to nothing. */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        spacing={4}
      >
        <Stack direction="row" spacing={3} alignItems="baseline">
          <Typography variant="body1" color="text.primary">
            Enterprise
          </Typography>
          {/* Sits where every column above shows a price, because that is where the
              eye goes. "Custom pricing" answers the question; a blank leaves the
              reader deciding whether the number failed to load. */}
          <Typography variant="body3" color="text.secondary">
            Custom pricing
          </Typography>
        </Stack>
        {/* SECONDARY, not primary (§26/§33: one primary per surface, and it is
            the page's main action). On signup step 3 that action is "Continue to
            checkout"; a filled Contact sales sitting above it gave the step two
            competing primaries, and the louder one pointed AWAY from the flow.
            Self-selecting into Enterprise is the minority path — it should be
            available, not advertised over the thing most visitors came to do.
            The same holds on the billing page, where the primaries are the
            per-column Subscribe buttons. */}
        <Button variant="secondary" onClick={onContact} sx={{ minWidth: 160 }}>
          Contact sales
        </Button>
      </Stack>

      <Typography
        variant="body3"
        color="text.secondary"
        sx={{ display: "block", mt: 2, lineHeight: 1.5 }}
      >
        {topPricedPlanTitle
          ? `Everything in ${topPricedPlanTitle}, plus the commitments a procurement or security team asks for.`
          : "Everything in the top plan, plus the commitments a procurement or security team asks for."}
      </Typography>

      {/* Separates the pitch from the specifics, so the card reads as two blocks
          rather than one run of text. Same `divider` the matrix rows use. */}
      <Box
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          mt: 4,
          pt: 4,
        }}
      >
        {/* Two columns, not one. As a single rail these four sat in the left third
            of a full-width card, leaving most of it empty. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
            },
            columnGap: 8,
            rowGap: 3,
          }}
        >
          {ENTERPRISE_INCLUDES.map((item) => (
            <Stack key={item} direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ pt: "1px", flexShrink: 0 }}>
                {/* grey[500], the SAME neutral the comparison matrix uses for its
                    own included-ticks. Brand purple here made the card read as a
                    third meaning for a colour that already says "your current
                    plan" on this page. */}
                <CircleCheckBigIcon
                  size={16}
                  strokeWidth={1.5}
                  color={theme.palette.grey[500]}
                />
              </Box>
              <Typography variant="body3" color="text.primary">
                {item}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Box>
    </Card>
  );
};
